<template>
  <div class="tw-flex tw-flex-wrap tw-items-center tw-space-x-6">
    <BaseButton
      v-if="isUserGroupMember"
      color="outline-red"
      @click.stop.prevent="openModal(confirmationModalName)"
      :is-disabled="leaveGroupForm.processing"
    >
      <LoadingCircle
        v-if="leaveGroupForm.processing"
        class="tw-mr-2"
        size="16"
      />
      <LeaveIcon v-else class="tw-mr-2 tw-h-4 tw-w-4" />
      Leave squad
    </BaseButton>

    <BaseButton v-else-if="isUserPendingGroupMember" disabled>
      <InviteSentIcon class="tw-mr-2 tw-h-4 tw-w-4" />
      Invite request sent...
    </BaseButton>

    <template v-else>
      <BaseButton
        v-if="hasEmptySpotsLeft"
        @click.stop.prevent="handleRequestInviteButtonClick"
      >
        Request Invite
      </BaseButton>
      <div
        class="tw-select-none tw-text-sm tw-font-medium tw-text-primary-500"
        v-tooltip="group.criteria"
      >
        Criteria to join
      </div>
    </template>

    <ConfirmationModal
      :name="confirmationModalName"
      header="Are you sure you want to leave this squad?"
      confirmation-button-text="Yes, leave squad"
      @confirmation="leaveGroup"
    >
      <template #icon>
        <LeaveIcon />
      </template>
    </ConfirmationModal>
    <RequestInviteModal
      :name="requestModalName"
      :apply-path="group.applyPath"
    />
  </div>
</template>

<script setup>
import ConfirmationModal from '@/shared/components/modals/ConfirmationModal.vue'
import BaseButton from '@/shared/components/BaseButton.vue'
import LeaveIcon from '@/assets/images/icons/leave.svg'
import InviteSentIcon from '@/assets/images/icons/invite-sent.svg'
import { UNLOCK_FULL_ACCESS_MODAL_NAME } from '@/shared/data/globalVariables.js'
import RequestInviteModal from '@/modules/growthmentor/groups/components/RequestInviteModal.vue'
import LoadingCircle from '@/shared/components/LoadingCircle.vue'
import {
  ACCESS_TYPE,
  OBJECT_PERMISSIONS,
  ROLE,
} from '@/shared/data/permissionSystem.js'
import { router, useForm } from '@inertiajs/vue3'
import { usePermissionSystem } from '@/shared/composables/usePermissionSystem.js'
import { computed } from 'vue'
import { useModals } from '@/shared/composables/useModals.js'
import { notify } from '@kyvg/vue3-notification'

const props = defineProps({
  group: {
    type: Object,
    required: true,
  },
})

const { openModal } = useModals()
const { hasObjectPermissions, hasRole, hasAccess } = usePermissionSystem()

const leaveGroupForm = useForm({})

const isUserGroupMember = computed(() => {
  return hasObjectPermissions(props.group, OBJECT_PERMISSIONS.GROUPS.APPROVED)
})
const isUserPendingGroupMember = computed(() => {
  return hasObjectPermissions(props.group, OBJECT_PERMISSIONS.GROUPS.REQUESTED)
})
const requestModalName = computed(() => {
  return `group-request-invite-modal-${props.group.id}`
})
const confirmationModalName = computed(() => {
  return `group-leave-confirmation-modal-${props.group.id}`
})
const hasEmptySpotsLeft = computed(() => {
  return props.group.maxMembers - props.group.currentMembersCount > 0
})

const handleRequestInviteButtonClick = () => {
  if (hasRole(ROLE.GUEST)) {
    openModal(UNLOCK_FULL_ACCESS_MODAL_NAME)
  } else if (hasAccess(ACCESS_TYPE.NONE)) {
    router.visit('/upgrade_plan/new')
  } else {
    openModal(requestModalName.value)
  }
}

const leaveGroup = () => {
  leaveGroupForm.delete(props.group.leavePath, {
    preserveScroll: true,
    onSuccess: () => {
      notify({
        type: 'success',
        text: 'You have left the squad.',
      })
    },
  })
}
</script>
